import React, { useEffect, useState, useCallback } from "react";
import Header from "../../common/Header";
import Sidebar from "../../common/Sidebar";
import Footer from "../../common/Footer";
import PageHeader from "../../pages/PageHeader";
import axios from "axios";
import { triggerAlert } from "../../utils";
import Loader from "../../common/Loader";
import DataTable from "react-data-table-component";
import Cookies from "js-cookie";

/**
 * Settings > Plan Audit
 *
 * Read-only diagnostic view of billing.AgentPlan rows. Pricing and marketplace
 * copy are edited on the entity's own configuration page (Calling Agents /
 * Avatar Chatbots / AI Workflows); phone-number rentals live on the dedicated
 * Phone Number Pricing settings page. This page supports bulk
 * activate/deactivate toggles only.
 */

// Plan Audit — phone_number is intentionally omitted; those rows are managed
// on the dedicated Settings → Phone Number Pricing page.
const CATEGORY_OPTIONS = [
  { value: "calling", label: "Calling" },
  { value: "avatar", label: "Avatar" },
  { value: "workflow", label: "Workflow" },
];

const categoryLabel = (value) => {
  const found = CATEGORY_OPTIONS.find((c) => c.value === value);
  return found ? found.label : value;
};

export default function AgentPlansList() {
  document.title = "Digital Employees - Plan Audit";
  const mainHeading = "Plan Audit";
  const heading = "Settings / Plan Audit";

  const [isLoading, setIsLoading] = useState(true);
  const [listData, setListData] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState("calling");

  const authHeaders = () => ({
    headers: {
      Authorization: `Bearer ${Cookies.get("admin_user_token")}`,
      "Content-Type": "application/json",
    },
  });

  const fetchListData = useCallback(async () => {
    setIsLoading(true);
    try {
      const query = categoryFilter ? `?category=${categoryFilter}` : "";
      const response = await axios.get(
        `/api/admin/agent-plans/${query}`,
        authHeaders()
      );
      // The viewset returns a plain list; tolerate a paginated shape too.
      const rows = Array.isArray(response.data)
        ? response.data
        : response.data?.results || [];
      rows.forEach((item, i) => {
        item.Num = i + 1;
      });
      setListData(rows);
    } catch (error) {
      setListData([]);
      triggerAlert("error", "Oops...", "Failed to load agent plans.");
    } finally {
      setIsLoading(false);
    }
  }, [categoryFilter]);

  useEffect(() => {
    fetchListData();
  }, [fetchListData]);

  const toggleStatus = async (row) => {
    try {
      await axios.patch(
        `/api/admin/agent-plans/${row.id}/`,
        { is_active: !row.is_active },
        authHeaders()
      );
      triggerAlert("success", "Success", "Status updated.");
      fetchListData();
    } catch (error) {
      triggerAlert("error", "Oops...", "Could not update status.");
    }
  };

  const columns = [
    {
      name: "S.No",
      width: "70px",
      selector: (row) => row.Num,
      cell: (row) => <span>{row.Num}</span>,
    },
    {
      name: "Agent / Plan Name",
      sortable: true,
      selector: (row) => row.agent_name,
      cell: (row) => <span>{row.agent_name}</span>,
    },
    {
      name: "Category",
      sortable: true,
      selector: (row) => row.category,
      cell: (row) => <span>{categoryLabel(row.category)}</span>,
    },
    {
      name: "Plan Key",
      sortable: true,
      selector: (row) => row.agent_key,
      cell: (row) => (
        <span style={{ fontFamily: "monospace", fontSize: "13px" }}>
          {row.agent_key}
        </span>
      ),
    },
    {
      name: "Units / Pack",
      sortable: true,
      selector: (row) => row.pack_units,
      cell: (row) => (
        <span>
          {row.pack_units} {row.unit_label || ""}
        </span>
      ),
    },
    {
      name: "Price",
      sortable: true,
      selector: (row) => Number(row.pack_price),
      cell: (row) => (
        <span style={{ fontWeight: 600 }}>
          ${Number(row.pack_price || 0).toFixed(2)}
          {row.category === "phone_number" ? " /mo" : ""}
        </span>
      ),
    },
    {
      name: "Status",
      sortable: true,
      selector: (row) => row.is_active,
      cell: (row) =>
        row.is_active ? (
          <span className="btn btn-success btn-sm activebtn btn-rounded waves-effect waves-light">
            Active
          </span>
        ) : (
          <span className="btn btn-danger btn-sm Inactivebtn btn-rounded waves-effect waves-light">
            Inactive
          </span>
        ),
    },
    {
      name: "Action",
      cell: (row) => (
        <button
          type="button"
          className={`btn btn-sm ${
            row.is_active ? "btn-outline-danger" : "btn-outline-success"
          }`}
          onClick={() => toggleStatus(row)}
        >
          {row.is_active ? "Deactivate" : "Activate"}
        </button>
      ),
    },
  ];

  const customStyles = {
    table: { style: { backgroundColor: "#ffffff", borderRadius: "8px" } },
    headRow: {
      style: {
        backgroundColor: "#064c77",
        borderBottom: "1px solid #dee2e6",
        minHeight: "52px",
      },
    },
    headCells: {
      style: {
        color: "#ffffff",
        fontSize: "13px",
        fontWeight: "700",
        textTransform: "uppercase",
      },
    },
    rows: {
      style: {
        minHeight: "56px",
        backgroundColor: "#ffffff",
        "&:hover": { backgroundColor: "#f8f9fa", cursor: "pointer" },
      },
    },
    cells: {
      style: {
        fontSize: "14px",
        color: "#495057",
        borderBottom: "1px solid #f1f3f5",
      },
    },
    pagination: {
      style: { borderTop: "1px solid #dee2e6", padding: "8px" },
    },
    noData: {
      style: {
        backgroundColor: "#ffffff",
        color: "#6c757d",
        padding: "24px",
      },
    },
  };

  return (
    <>
      <div id="layout-wrapper">
        <Header />
        <Sidebar />

        <div className="main-content">
          <div className="page-content" style={{ backgroundColor: "#ededed" }}>
            <div className="container-fluid">
              <PageHeader mainHeading={mainHeading} heading={heading} />

              <div className="row">
                <div className="col-12">
                  <div className="card">
                    <div className="card-body">
                      <div className="row align-items-center mb-3">
                        <div className="col-md-4">
                          <label className="form-label mb-1 font_weight_500">
                            Filter by category
                          </label>
                          <select
                            className="form-select"
                            value={categoryFilter}
                            onChange={(e) => setCategoryFilter(e.target.value)}
                          >
                            <option value="">All categories</option>
                            {CATEGORY_OPTIONS.map((c) => (
                              <option key={c.value} value={c.value}>
                                {c.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="alert alert-warning py-2" role="alert">
                        <strong>Audit view.</strong> Pricing and marketplace
                        copy should be edited on the entity's own page —
                        Configurations → Calling Agents / Avatar Chatbots / AI
                        Workflows. Phone-number rentals live under{" "}
                        <strong>Settings → Phone Number Pricing</strong>. Use
                        this page for diagnostics, bulk toggles, and fixing
                        broken links only.
                      </div>

                      {!isLoading ? (
                        <DataTable
                          columns={columns}
                          data={listData}
                          pagination
                          fixedHeader
                          fixedHeaderScrollHeight="600px"
                          persistTableHead
                          customStyles={customStyles}
                        />
                      ) : (
                        <div className="loader-overlay text-white">
                          <Loader />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Footer />
        </div>
      </div>

    </>
  );
}
